import { FC, ReactNode } from 'react'
import dayjs from 'dayjs'
import { Select } from 'antd'
import { CheckBoxLabel } from '@UI/CheckBox'
import InfoComp from '@pages/Admin/CRMSettings/components/InfoComp/InfoComp'
import SliderField from '@pages/Admin/CourseSettings/components/SliderField/SliderField'
import { getTariffValueMin } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/components/SelectTariffValue/SelectTariffValue'
import CourseDiscountModeFields from '../../../../../CourseDiscountModeFields'
import type { CrmLinkDiscountMode } from '../../../../../discountMode.utils'
import {
  getMonthlyCourseLinkBasePrice,
  getMonthlyCourseLinkDaysInMonth,
  getMonthlyCourseLinkMonthUnitPrice,
  type MonthlyCourseLinkPeriodUnit,
} from '../../../../../monthlyCourseLinkDiscount.utils'
import CreateLinkNumberInput from '../../../../../CreateLinkNumberInput'
import classes from '../index.module.css'
import spclasses from '../../../../../../SetCourseClassDirectionModal/purchase.module.css'
import { CREATE_LINK_BLOCK_TARIFF_TYPES } from '../../../../../tariffTypes'
import { withCreateLinkBlockTariffTypesHint } from '../../../../../createLinkBlockInfoContent'

export type MonthlyAdditionalCourseFieldsProps = {
  isPeriodFixed: boolean
  periodUnit: MonthlyCourseLinkPeriodUnit
  periodValue: number | null
  price: number
  tariff: ICourseTariff
  enrollDate?: string | null
  discount: number
  discountMode: CrmLinkDiscountMode | null
  fullBasePrice: number
  suggestedPrice: number
  priceManuallyEdited: boolean
  onTogglePeriodFixed: (checked?: boolean) => void
  onChangePeriodUnit: (value: MonthlyCourseLinkPeriodUnit) => void
  onChangeValue: (value: string | number | null) => void
  onChangeUnfixedValue: (value: number) => void
  onChangeDiscount: (value: string | number | null) => void
  onDiscountModeChange: (mode: CrmLinkDiscountMode | null) => void
  onChangePrice: (value: string | number | null) => void
  onResetSuggestedPrice: () => void
}

/**
 * UI доп. курса с помесячным тарифом: фиксация периода, discount_mode, цена.
 */
const MonthlyAdditionalCourseFields: FC<MonthlyAdditionalCourseFieldsProps> = ({
  isPeriodFixed,
  periodUnit,
  periodValue,
  price,
  tariff,
  enrollDate,
  discount,
  discountMode,
  fullBasePrice,
  suggestedPrice,
  priceManuallyEdited,
  onTogglePeriodFixed,
  onChangePeriodUnit,
  onChangeValue,
  onChangeUnfixedValue,
  onChangeDiscount,
  onDiscountModeChange,
  onChangePrice,
  onResetSuggestedPrice,
}) => {
  const resolvedPeriodValue = periodValue ?? 1
  const firstMonthPrice = getMonthlyCourseLinkBasePrice(tariff?.first_price)
  const monthUnitPrice = getMonthlyCourseLinkMonthUnitPrice(
    tariff,
    resolvedPeriodValue,
  )
  const daysInMonth = getMonthlyCourseLinkDaysInMonth(enrollDate)
  const paymentStartDate =
    enrollDate && dayjs(enrollDate).isValid() ? dayjs(enrollDate) : dayjs()
  const paymentEndDate =
    periodUnit === 'day'
      ? paymentStartDate.add(resolvedPeriodValue - 1, 'day')
      : paymentStartDate.add(resolvedPeriodValue, 'month').subtract(1, 'day')
  const tariffDescription =
    periodUnit === 'day'
      ? `Тариф: ${firstMonthPrice} ₽/мес. В месяце расчёта: ${daysInMonth} дн.`
      : `Тариф: ${monthUnitPrice} ₽/мес × ${resolvedPeriodValue} мес.`
  const sliderMin = getTariffValueMin(tariff)

  const renewalDiscountSlot: ReactNode = isPeriodFixed ? (
    <CourseDiscountModeFields
      variant="renewal_only"
      value={discount || null}
      onChange={onChangeDiscount}
      discountMode={discountMode}
      onDiscountModeChange={onDiscountModeChange}
      fullBasePrice={fullBasePrice}
    />
  ) : null

  return (
    <div className={classes.monthlyFields}>
      <div className={spclasses.inputWithLabel}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CheckBoxLabel
            type="button"
            value={isPeriodFixed}
            onClick={onTogglePeriodFixed}
            label="Зафиксировать период оплаты"
            className={spclasses.checkboxWithLabel}
            style={{
              justifyContent: 'flex-start',
              paddingTop: '4px',
              paddingBottom: '4px',
              height: 'auto',
            }}
          />
          <InfoComp
            text={withCreateLinkBlockTariffTypesHint(
              'По умолчанию клиент при покупке сам выбирает, на какой период ему оплатить обучение. При включении данного чекбокса вы фиксируете период оплаты.',
              CREATE_LINK_BLOCK_TARIFF_TYPES.monthly,
            )}
          />
        </div>
      </div>

      {isPeriodFixed ? (
        <div className={classes.monthlyFieldsRow}>
          <div className={spclasses.inputWithLabel}>
            <div className={spclasses.inputLabel}>Единица периода</div>
            <Select
              className={spclasses.selectField}
              popupClassName={spclasses.selectFieldPopup}
              value={periodUnit}
              options={[
                { value: 'month', label: 'Месяцы' },
                { value: 'day', label: 'Дни' },
              ]}
              onChange={onChangePeriodUnit}
            />
          </div>
          <div className={spclasses.inputWithLabel}>
            <CreateLinkNumberInput
              className={spclasses.input}
              title={
                periodUnit === 'day' ? 'Количество дней' : 'Количество месяцев'
              }
              integer
              min={1}
              value={periodValue}
              onChange={onChangeValue}
            />
          </div>
        </div>
      ) : (
        <div style={{ paddingLeft: '8px', paddingTop: '4px' }}>
          <SliderField
            label="Период"
            value={periodValue ?? sliderMin}
            extraContent={(periodValue ?? sliderMin).toString()}
            min={sliderMin}
            max={24}
            onChange={onChangeUnfixedValue}
          />
        </div>
      )}

      {!isPeriodFixed ? (
        <CourseDiscountModeFields
          variant="first_payment"
          value={discount || null}
          onChange={onChangeDiscount}
          discountMode={discountMode}
          onDiscountModeChange={onDiscountModeChange}
          fullBasePrice={fullBasePrice}
        />
      ) : null}

      {renewalDiscountSlot}

      {isPeriodFixed ? (
        <div className={spclasses.inputWithLabel}>
          <CreateLinkNumberInput
            className={spclasses.input}
            title="Итого по доп. курсу"
            min={0}
            step={0.01}
            value={price}
            onChange={onChangePrice}
          />
          <div className={classes.priceDescription}>
            Расчётная сумма: {suggestedPrice} ₽
            <div>{tariffDescription}</div>
            <div>
              Оплачено с {paymentStartDate.format('DD.MM.YYYY')} по{' '}
              {paymentEndDate.format('DD.MM.YYYY')}
            </div>
            {priceManuallyEdited ? (
              <button
                type="button"
                className={classes.resetPriceButton}
                onClick={onResetSuggestedPrice}
              >
                вернуть расчётную
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className={classes.priceDescription} style={{ paddingLeft: 8 }}>
          Расчётная сумма: {suggestedPrice} ₽
          <div>{tariffDescription}</div>
          <div>
            Оплачено с {paymentStartDate.format('DD.MM.YYYY')} по{' '}
            {paymentEndDate.format('DD.MM.YYYY')}
          </div>
        </div>
      )}
    </div>
  )
}

export default MonthlyAdditionalCourseFields
